import { useCallback, useEffect } from "react";
import { PricingProfile, CreatePricingProfile, UpdatePricingProfile } from "../types/models/PricingProfile.d";
import { UpdateEntityPricingProfile } from "../types/models/PricingProfile";
import { useGetPricingProfileListQuery, useInsertPricingProfileMutation, useUpdatePricingProfileMutation, useDeletePricingProfileMutation, } from "../apis/PricingProfile.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function usePricingProfile(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertPricingProfileMutation, { 
        isLoading: insertPricingProfileLoading, 
        isSuccess: insertPricingProfileIsSuccess,
        isError: insertPricingProfileIsError,
        error: insertPricingProfileError,
    }] = useInsertPricingProfileMutation()
    const [updatePricingProfileMutation, { 
        isLoading: updatePricingProfileLoading,
        isSuccess: updatePricingProfileIsSuccess,
        isError: updatePricingProfileIsError,
        error: updatePricingProfileError,
    }] = useUpdatePricingProfileMutation()
    const [deletePricingProfileMutation, { 
        isLoading: deletePricingProfileLoading,
        isSuccess: deletePricingProfileIsSuccess,
        isError: deletePricingProfileIsError,
        error: deletePricingProfileError,
    }] = useDeletePricingProfileMutation()

    const loading =
        insertPricingProfileLoading ||
        updatePricingProfileLoading ||
        deletePricingProfileLoading ||
        false

    const insertPricingProfile = useCallback(async (pricingProfile: CreatePricingProfile, redirectPath: string|undefined) => {
        try {
            const data = await insertPricingProfileMutation(pricingProfile).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertPricingProfileMutation, navigate])
    
    useEffect(() => {
        if (insertPricingProfileIsSuccess) {
            openSuccessAlert('Pricing Profile created')
        } else if (insertPricingProfileIsError) {
            console.log("add error", insertPricingProfileError)
            openFailAlert(t`Pricing Profile add error: ` + insertPricingProfileError?.data?.message)
        }
    }, [
        insertPricingProfileIsSuccess,
        insertPricingProfileIsError,
        insertPricingProfileError
    ]);

    const updatePricingProfile = useCallback(async (pricingProfile: UpdateEntityPricingProfile, redirectPath: string|undefined) => {
        try {
            const data = await updatePricingProfileMutation(pricingProfile).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updatePricingProfileMutation, navigate])
    
    useEffect(() => {
        if (updatePricingProfileIsSuccess) {
            openSuccessAlert(t`Pricing Profile updated`)
        } else if (updatePricingProfileIsError) {
            console.log("update error", updatePricingProfileError)
            openFailAlert(t`Pricing Profile update error: ` + updatePricingProfileError?.data?.message)
        }
    }, [
        updatePricingProfileIsSuccess,
        updatePricingProfileIsError,
        updatePricingProfileError
    ]);

    const deletePricingProfile = useCallback((pricingProfile: PricingProfile, redirectPath: string|undefined) => {
        openDialog(async () => {
            pricingProfile = {
                ...pricingProfile,
            }
            try {
                const { data } = await deletePricingProfileMutation(pricingProfile);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deletePricingProfileMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deletePricingProfileIsSuccess) {
            openSuccessAlert(t`Pricing Profile deleted`)
        } else if (deletePricingProfileIsError) {
            console.log("delete error", deletePricingProfileError)
            openFailAlert(t`Pricing Profile delete error: ` + deletePricingProfileError?.data?.message)
        }
    }, [
        deletePricingProfileIsSuccess,
        deletePricingProfileIsError,
        deletePricingProfileError
    ]);

    return {
        loading,
        insertPricingProfile,
        updatePricingProfile,
        deletePricingProfile
    }
}
